/**
 * STATISTICS — the per-article table (`listArticleStats`, added by decision-log row 141 applying
 * row 140) and who the readers were, one sealed month at a time (./AudiencePanel.tsx, register
 * R-020). The two have different filters on purpose - a range for the table, a month for the
 * audience (docs/ui/04-pages.md, 3.3c) - so each section keeps its own.
 *
 * FIVE THINGS THIS TABLE EXISTS TO GET RIGHT. Each is a way to be quietly, expensively wrong.
 *
 * 1. BOTH VIEW NUMBERS ARE ON EVERY ROW AND BOTH ARE LABELLED. `views` is the display counter the
 *    apps show; `countedViews` is the countable view that PAY uses — one per reader, per article,
 *    per UTC day. The schema says why in as many words: "An author comparing this table with their
 *    payment and finding one number will conclude the platform is wrong, which is why the table
 *    carries both and labels them." Showing one and calling it "views" is the failure mode.
 *
 * 2. `distinctReaders` IS ABSENT AND ITS ABSENCE IS DRAWN. There is no such field on
 *    `ArticleStats` at all — it is per-day and it does not sum, because "a reader who came back on
 *    three days would count three times". The column stays, the cell says nothing is there, and
 *    hovering explains why. Hiding the column would leave an author hunting for a number the
 *    Overview shows them; filling it in would be the exact error the omission prevents.
 *
 * 3. NOTHING IS COMPUTED HERE. "These are the daily cells the dashboard already sums, grouped per
 *    article instead of per author." No column total is printed, and this screen never adds two
 *    rows together — a per-article sum is a different query with a different answer.
 *
 * 4. ORDINAL 1 EARNS NOTHING, EVER (D7). The first article is marked, because a client that shows
 *    numbers beside money "needs it to explain a legitimate zero".
 *    ★ UNLESS IT WAS BROUGHT INTO THE CABINET (design 14, F2): the move numbered the earlier
 *    articles, so for such an author ordinal 1 is an old piece and their next new one earns. The
 *    walk of 2026-10-01 found this table calling such a piece "your first article, which earns
 *    nothing by design". Design 15's plain words say nothing about where it came from: with
 *    `importedFromOldApp` the row simply carries no ordinal words. The pay rule did not move.
 *
 * 4b. VIEWS ARE NAMED BY TIME (the owner, 2026-10-05: "for the author it is one app, one data").
 *    "Views" is the period's; "All time", beside it, every view the article ever had, both apps,
 *    one number - `allTimeViews`, read by ../../features/cabinet/all-time-views.ts, which takes
 *    `oldAppViews` from an older server and says "Not measured yet" with neither. Never paid (row 231),
 *    never added to the period's. Design 14's "1,234 earlier" under Views is gone, with every
 *    "earlier" about views in the notes: those views are inside the all-time figure now.
 *
 * 5. THE RANGE IS THE RESPONSE'S OWN. `ArticleStatsPage` carries `from` and `to`; this screen
 *    prints them rather than a range it chose, for the same clock reason as the Overview.
 *
 *    BOTH BOUNDS ARE INCLUSIVE, as on `getDashboard`: the contract says so of `listArticleStats.to`
 *    ("Inclusive upper bound", authors-api.yaml), and the server resolves both reads' default range
 *    with the one `resolveDayRange` (src/modules/analytics/cabinet/analytics-window.ts). This screen
 *    once printed "exclusive" from an older reading - wave 25c corrected it. Its FIRST read still
 *    sends NO range and prints the one it is given, and it never invites a comparison of totals
 *    across the two screens: a per-article column does not add up to a per-author tile (point 3).
 *
 * ★ DESIGN 15 (the owner's live look at real cabinets, 2026-10-01; the prototype's `cabIsDash`):
 *   - THREE SECTIONS AS TABS, "Overview", "Articles" and "Audience" - the prototype's `dashTabs`
 *     row, drawn as its pills. "Overview" (./StatsOverview.tsx) is every author-wide figure for a
 *     period with its change: the Overview SCREEN now draws only four tiles, and the rest it used
 *     to show lives here (the integrator's addition, 2026-10-01). Those tiles are `getDashboard`'s,
 *     the author's own totals, worked out over the period in one go - never a sum of this table's
 *     rows (point 3), and the table's notes still say so.
 *   - ONE `getDashboard` READ WITH NO RANGE, made here and shared: the Overview tab counts its
 *     periods back from its `to`, and the Audience tab its months (./AudiencePanel.tsx).
 *   - ONE PERIOD BUTTON IN THE TABS' ROW (design 24, T15; it replaced design 15's chips): the set
 *     periods and a custom range, each counted back from the server's own `to` - learnt from the
 *     first answer, which asked for no range - by ../../features/cabinet/ranges.ts, never from the
 *     browser's clock. The shown tab's own button lands in the row (`pickerSlot`); the Articles tab
 *     keeps the first answer's period ("Last 365 days") as its last entry, chosen at first.
 *   - THE TABLE'S SIDEWAYS SCROLL IS A FOCUSABLE, NAMED REGION. In the shell's narrower column the
 *     980px table scrolls, and axe failed `scrollable-region-focusable` on this page (a keyboard
 *     could not reach the hidden columns: no cell holds anything focusable).
 *   - PLAIN WORDS: no "UTC", no "platform", no "old apps" in front of the author.
 *
 * ★★ DESIGN 18 (the owner, 2026-10-05: "its bad shaped on UI/UX and components"; docs/design/
 *   18-statistics.md). The Overview tab is one chart under five headline figures, the articles that
 *   did best and the rest of the figures as a list (./StatsOverview.tsx); the Articles tab is
 *   ./statistics/ArticlesPanel.tsx - most views first, covers, a card per article on a phone, each
 *   article's own days. Its "Distinct readers" column is gone (ruling 2): points 1-5 above still
 *   hold - both view figures labelled, nothing computed, ordinal 1's words, views named by time, the
 *   range the answer's own - and point 2 is now one note under the table instead of an empty
 *   column. The tabs are held here so "All articles" on the Overview can open the Articles tab on
 *   its period and figure.
 */
import { Group, Stack, Tabs } from '@mantine/core';
import { useCallback, useState } from 'react';
import { useCabinetApi } from '../../features/cabinet/api';
import { CabinetPageHeader } from '../../features/cabinet/components';
import { isDay } from '../../features/cabinet/ranges';
import type { PeriodChoice } from '../../features/cabinet/ranges';
import { useAsync } from '../../features/cabinet/state';
import { useTranslation } from '../../i18n';
import { AudiencePanel } from './AudiencePanel';
import { StatsOverview } from './StatsOverview';
import { VIDEOS_TAB_QUERY } from '../videos/paths';
import { ArticlesPanel } from './statistics/ArticlesPanel';
import type { SortKey } from './statistics/ArticlesPanel';
import type { ArticleFigureKey } from './statistics/figures';
import classes from './articles.module.css';

export interface ArticleStatsRouteProps {
  articlesHref?: string;
}

type Section = 'overview' | 'articles' | 'videos' | 'audience';

/** "All articles" on the Overview: the Articles tab on that period, in that figure's order. */
interface ArticlesIntent {
  readonly period: PeriodChoice;
  readonly sortBy: SortKey;
  /** A new intent remounts the tab, so its period and order are the intent's, not the last ones. */
  readonly seq: number;
}

export function ArticleStatsRoute({ articlesHref = '/articles' }: ArticleStatsRouteProps) {
  const { t } = useTranslation('articles');
  const api = useCabinetApi();
  // The server's latest complete day and usual period: `getDashboard` with no range, read once for
  // the three tabs.
  const readLatest = useCallback(
    (signal: AbortSignal) => api.getDashboard(undefined, signal),
    [api],
  );
  const latest = useAsync(readLatest, 'statistics:default-range');
  const [section, setSection] = useState<Section>('overview');
  const [intent, setIntent] = useState<ArticlesIntent | undefined>(undefined);
  // Where the shown tab draws its period button: the tabs' own row (design 24, T15).
  const [pickerSlot, setPickerSlot] = useState<HTMLDivElement | null>(null);

  const openArticles = useCallback((period: PeriodChoice, sortBy: ArticleFigureKey) => {
    setIntent((last) => ({ period, sortBy, seq: (last?.seq ?? 0) + 1 }));
    setSection('articles');
  }, []);

  return (
    <Stack gap="xl">
      <CabinetPageHeader title={t('stats.title')} lede={t('stats.lede')} />

      <Tabs
        value={section}
        onChange={(next) => {
          if (
            next === 'overview' ||
            next === 'articles' ||
            next === 'videos' ||
            next === 'audience'
          ) {
            setSection(next);
          }
        }}
        unstyled
        classNames={{ list: classes.tabList, tab: classes.tab, panel: classes.panel }}
      >
        <Group justify="space-between" align="center" gap="sm" preventGrowOverflow={false}>
          <Tabs.List aria-label={t('stats.tabs.label')}>
            <Tabs.Tab value="overview">{t('stats.tabs.overview')}</Tabs.Tab>
            <Tabs.Tab value="articles">{t('stats.tabs.articles')}</Tabs.Tab>
            <Tabs.Tab value="videos">{t('stats.tabs.videos')}</Tabs.Tab>
            <Tabs.Tab value="audience">{t('stats.tabs.audience')}</Tabs.Tab>
          </Tabs.List>
          <div ref={setPickerSlot} />
        </Group>
        <Tabs.Panel value="overview">
          <StatsOverview
            latest={latest}
            onAllArticles={openArticles}
            pickerSlot={section === 'overview' ? pickerSlot : null}
          />
        </Tabs.Panel>
        <Tabs.Panel value="articles">
          {/* The table reads on its own: it never waits on - or fails with - the read above. */}
          <ArticlesPanel
            key={intent?.seq ?? 0}
            usual={
              latest.data !== undefined && isDay(latest.data.from) && isDay(latest.data.to)
                ? { from: latest.data.from, to: latest.data.to }
                : undefined
            }
            articlesHref={articlesHref}
            initial={
              intent === undefined ? undefined : { period: intent.period, sortBy: intent.sortBy }
            }
            pickerSlot={section === 'articles' ? pickerSlot : null}
          />
        </Tabs.Panel>
        <Tabs.Panel value="videos">
          {/* Read only when opened: most authors look at their articles. */}
          {section === 'videos' ? (
            <ArticlesPanel
              kind="video"
              usual={
                latest.data !== undefined && isDay(latest.data.from) && isDay(latest.data.to)
                  ? { from: latest.data.from, to: latest.data.to }
                  : undefined
              }
              articlesHref={`${articlesHref}?${VIDEOS_TAB_QUERY}`}
              pickerSlot={pickerSlot}
            />
          ) : null}
        </Tabs.Panel>
        <Tabs.Panel value="audience">
          <AudiencePanel
            serverDay={latest}
            pickerSlot={section === 'audience' ? pickerSlot : null}
          />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}
